// @ts-strict-ignore
import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subscription, throwError } from 'rxjs';
import {
  SohoAlertDirective,
  SohoBusyIndicatorDirective,
  SohoSpinboxComponent
} from 'ids-enterprise-ng';
import { LMPrintDialog, LMPrintPdfInfo } from '@modal/print-dialog/lm-print-dialog.service';
import { LMPrintRequestService } from '@modal/print-dialog/lm-print-request.service';
import { AppConstants } from '@utils/app.constants';
import { LMLookupBasicComponent } from '@field/lookup';
import { LMFieldValueChangedEvent, LMFormRequestService } from '@app/components';
import {
  LMContextService,
  LMFormatUtilService,
  LMModalOpenService,
  LMModalWrapperService,
  LMUrlUtilService
} from '@app/services';
import { ListResource } from '@app/resources';
import { LMListDropdownComponent } from '@components/lmlistdropdown';
import { LMGlobalPersonalizationService } from '@app/states';
import { catchError } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { LMSecurityUtilService } from '@app/utils/lm-security-util.service';

@Component({
  selector: 'lm-print-pdf-dialog',
  standalone: false,
  templateUrl: './lm-print-pdf-dialog.component.html',
  styleUrls: [ './lm-print-pdf-dialog.component.css' ]
  })
export class LMPrintPdfDialogComponent implements LMPrintDialog, AfterViewInit, OnDestroy, OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmPrintRequestService: LMPrintRequestService,
    private lmFromRequestService: LMFormRequestService,
    private lmFormatUtilService: LMFormatUtilService,
    private http: HttpClient,
    private changeDetectionRef: ChangeDetectorRef,
    private lmContextService: LMContextService,
    private lmModalWrapperService: LMModalWrapperService,
    private lmModalOpenService: LMModalOpenService,
    private lmSecurityUtilService: LMSecurityUtilService
  ) {}

  @ViewChild('modalPlaceholder', { read: ViewContainerRef, static: true }) modalPlaceholder: ViewContainerRef;
  @ViewChild('maxLevels') maxLevels: SohoSpinboxComponent;
  @ViewChild('maxRows') maxRows: SohoSpinboxComponent;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChildren(SohoAlertDirective) alerts: QueryList<SohoAlertDirective>;
  @ViewChild(forwardRef(() => LMLookupBasicComponent)) lmLookupBasicPdf;
  @ViewChild('dynamicActorsComponent') dynamicActorsComponent: LMListDropdownComponent;
  @Input() printInfo: LMPrintPdfInfo;


  pdfType: string;
  private subscriptions: Subscription[] = [];

  dynamicActorOptions: LMListDropdownOptions = {
    businessClass: 'Actor',
    maxValues: 20,
    searchFields: [ 'Actor', 'PersonName.GivenName',  'PersonName.FamilyName', 'PersonName.PreferredGivenName' ],
    selectList: 'AdHocDistributionGroupActorList' };
  showDynamicActors = false;
  atLeastOneDynamicActor = false;
  dynamicActorsLabel = Soho.Locale.translate('PDFAdHocActors');

  printForm: FormGroup;
  includeSearchForm = false;
  includePrintOption = false;
  reportDistGroupLabel = '';
  sourceFieldName = '';
  allRecordsAlert: SohoAlertDirective;
  dynamicActorsAlert: SohoAlertDirective;
  useCompactFormLayout = false;
  useTabs = true;
  showMaxRows = false;
  showFontOffset = false;
  showRetentionDuration = false;
  showMaxLevels = false;
  showCustomPageSize = false;
  pdfSizeData: Array<LMPdfPageSize>;
  public printOptions: LMPrintOption[] = [];

  private allRecordsAlertId = 'print-pdf-dialog-all-records-alert';
  private dynamicActorsAlertId = 'print-pdf-dialog-dynamic-actors-alert';

  ngOnInit() {
    this.useCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;

    // Setup form with default values
    this.printForm = this.formBuilder.group({
      name: [ '', Validators.required ],
      orientation: [ 'landscape', Validators.required ],
      fontOffsetOption: [ 'default', Validators.required ],
      fontOffset: [ 0, Validators.required ],
      retentionDurationOption: [ 'default', Validators.required ],
      retentionDuration: [ 0, Validators.required ],
      maxRows: [ 5000, Validators.required ],
      emptyReportOptions: [ 'default', Validators.required ],
      recordLimitOption: [ 'default', Validators.required ],
      allRecords: [ false, Validators.required ],
      searchForm: [ { value: false, disabled: true }, Validators.required ],
      printOption: [ { value: 0, disabled: true }, Validators.required ],
      maxLevels: [ { value: 3, disabled: true }, Validators.required ],
      pdfPageSize: [ '', Validators.required ],
      pdfPageWidth: [ '', [Validators.required, Validators.min(1), Validators.pattern('^\\d{1,5}(\\.\\d{1,2})?$')]],
      pdfPageHeight: [ '', [Validators.required, Validators.min(1), Validators.pattern('^\\d{1,5}(\\.\\d{1,2})?$')] ],
      pdfPageUOM: ['Inch', Validators.required]
    });
    const controls = this.printForm.controls;

    this.pdfType = this.printInfo.pdfType;
    if (this.pdfType === AppConstants.REGULAR_FORM_PDF && !this.isUseAllActorGroups) {
      this.useTabs = false;
    }

    controls.name.setValue(this.printInfo.name);

    if (this.printInfo.fontOffset) {
      controls.fontOffset.setValue(this.printInfo.fontOffset);
      if (this.printInfo.fontOffset === 0) {
        controls.fontOffsetOption.setValue('default');
      } else if (this.printInfo.fontOffset === -2) {
        controls.fontOffsetOption.setValue('small');
      } else if (this.printInfo.fontOffset === -3) {
        controls.fontOffsetOption.setValue('smaller');
      } else if (this.printInfo.fontOffset === -5) {
        controls.fontOffsetOption.setValue('smallest');
      } else {
        controls.fontOffsetOption.setValue('custom');
        this.showFontOffset = true;
      }
    } else {
      controls.fontOffsetOption.setValue('default');
    }

    if (this.printInfo.retentionDuration) {
      controls.retentionDuration.setValue(this.printInfo.retentionDuration);
      if (this.printInfo.retentionDuration === 0 || this.printInfo.retentionDuration === 180) {
        controls.retentionDurationOption.setValue('default');
      } else if (this.printInfo.retentionDuration === 7) {
        controls.retentionDurationOption.setValue('one-week');
      } else if (this.printInfo.retentionDuration === 30) {
        controls.retentionDurationOption.setValue('one-month');
      } else if (this.printInfo.retentionDuration === 365) {
        controls.retentionDurationOption.setValue('one-year');
      } else if (this.printInfo.retentionDuration === 1825) {
        controls.retentionDurationOption.setValue('five-years');
      } else {
        controls.retentionDurationOption.setValue('custom');
        this.showRetentionDuration = true;
      }
    } else {
      controls.retentionDurationOption.setValue('default');
    }


    if (this.printInfo.orientation) {
      controls.orientation.setValue(this.printInfo.orientation);
    }

    this.getPdfSizeList().subscribe((response: any) => {
      if (response.dataViewSet?.data.length > 0) {
        this.pdfSizeData = this.getPdfSizeData(response.dataViewSet);
        if (this.pdfSizeData && this.pdfSizeData.length > 0) {
          controls.pdfPageSize.setValue(this.printInfo.paperSize || this.pdfSizeData[0].value);
        }
      }
    });

    if (this.printInfo.customWidth) {
      controls.pdfPageWidth.setValue(this.printInfo.customWidth);
    }

    if (this.printInfo.customHeight) {
      controls.pdfPageHeight.setValue(this.printInfo.customHeight);
    }

    if (this.printInfo.customUnit) {
      controls.pdfPageUOM.setValue(this.printInfo.customUnit);
    }

    this.includeSearchForm = this.printInfo.includeSearchForm;
    this.includePrintOption = (
      this.pdfType === AppConstants.COMPOSITE_FORM_PDF ||
      this.pdfType === AppConstants.LIST_WITH_DETAILS_PDF ||
      this.pdfType === AppConstants.TREE_LIST_PDF ||
      this.pdfType === AppConstants.TREE_LIST_WITH_DETAILS_PDF ||
      this.pdfType === AppConstants.LIST_WITH_EXPANDABLE_CARD_ROW_PDF
    );

    if (!this.printInfo.includeRecordLimit) {
      controls.allRecords.disable();
      controls.recordLimitOption.disable();
      controls.maxRows.disable();
    }

    if (this.includeSearchForm) {
      controls.searchForm.enable();
      if (this.printInfo.filterFields?.length) {
        controls.searchForm.setValue(true);
      }
    }

    if (this.includePrintOption) {
      this.printOptions = this.getPrintOptions();
      controls.printOption.enable();
      // Auto select first option
      controls.printOption.setValue(this.printOptions[0].value);
    }

    this.reportDistGroupLabel = Soho.Locale.translate('PDFRepDistGroup');
    this.sourceFieldName = 'ReportDistributionGroup';
  }

  ngAfterViewInit() {
    const controls = this.printForm.controls;
    let subscription: Subscription;
    // PrintOption value determines if maxLevels field enable/disable
    subscription = controls.printOption.valueChanges.subscribe((value) => {
      const maxLevelsControl = controls.maxLevels;
      const disable = !this.isSpecifiedLevels(Number(value));
      this.showMaxLevels = !disable;
      if (disable) {
        maxLevelsControl.disable();
      } else {
        maxLevelsControl.enable();
      }
    });
    this.subscriptions.push(subscription);

    // AllRecords value determines if maxRows field enable/disable
    subscription = controls.recordLimitOption.valueChanges.subscribe((value) => {
      const maxRowsControl = this.printForm.get('maxRows');
      const allRecordsControl = this.printForm.get('allRecords');
      allRecordsControl.setValue(value === 'all');
      this.showMaxRows = value === 'custom';
      this.allRecordsAlert?.removeAllMessages();

      switch (value) {
        case 'all':
          maxRowsControl.disable();
          const alertMessage =  Soho.Locale.translate('PDFIncludingAllRecords');
          this.allRecordsAlert?.addInlineMessage(alertMessage, 'info', true, undefined, undefined, this.allRecordsAlertId);
          break;
        case 'default':
          maxRowsControl.enable();
          maxRowsControl.setValue(5000);
          break;
        case 'large':
          maxRowsControl.enable();
          maxRowsControl.setValue(250000);
          break;
        case 'small':
          maxRowsControl.enable();
          maxRowsControl.setValue(100);
          break;
        case 'custom':
          maxRowsControl.enable();
          maxRowsControl.setValue(5000);
      }
    });
    this.subscriptions.push(subscription);


    subscription = controls.fontOffsetOption.valueChanges.subscribe((value) => {
      const fontOffsetControl = this.printForm.get('fontOffset');
      this.showFontOffset = value === 'custom';
      fontOffsetControl.enable();

      switch (value) {
        case 'default':
          fontOffsetControl.setValue(0);
          break;
        case 'small':
          fontOffsetControl.setValue(-2);
          break;
        case 'smaller':
          fontOffsetControl.setValue(-3);
          break;
        case 'smallest':
          fontOffsetControl.setValue(-5);
          break;
        case 'custom':
          fontOffsetControl.setValue(0);
          break;
      }
    });
    this.subscriptions.push(subscription);

    subscription = controls.retentionDurationOption.valueChanges.subscribe((value) => {
      const retentionDurationControl = this.printForm.get('retentionDuration');
      this.showRetentionDuration = value === 'custom';
      retentionDurationControl.enable();

      switch (value) {
        case 'default':
          retentionDurationControl.setValue(0);
          break;
        case 'one-week':
          retentionDurationControl.setValue(7);
          break;
        case 'one-month':
          retentionDurationControl.setValue(30);
          break;
        case 'one-year':
          retentionDurationControl.setValue(365);
          break;
        case 'five-years':
          retentionDurationControl.setValue(1825);
          break;
        case 'custom':
          // retentionDurationControl.setValue(0);
          break;
      }
    });
    this.subscriptions.push(subscription);

    subscription = controls.pdfPageSize.valueChanges.subscribe((value) => {
      if (value === 'Custom') {
        this.showCustomPageSize = true;
      } else {
        this.showCustomPageSize = false;
      }
    });
    this.subscriptions.push(subscription);

    if (this.lmLookupBasicPdf) {
      // Set the select list url for ReportDistributionGroup
      this.lmLookupBasicPdf.viewName = 'PDFOptions'; // Dummy data for handcoded forms. (There is no formSpec or fromDataView)
      this.lmLookupBasicPdf.selectListUrl = this.lmPrintRequestService.getReportDistributionSelectUrl();
    }
    this.allRecordsAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'recordLimitAlert');
    this.dynamicActorsAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'dynamicActors');

    if (this.printInfo.includeRecordLimit) {
      const exportDefaultRecordLimit = this.lmGlobalPersonalizationService.getGlobalPersonalization().exportDefaultRecordLimit;
      if (exportDefaultRecordLimit === 'no-limit') {
        controls.recordLimitOption.setValue('all');
        controls.maxRows.disable();
        const alertMessage =  Soho.Locale.translate('PDFIncludingAllRecords');
        this.allRecordsAlert?.addInlineMessage(alertMessage, 'info', true, undefined, undefined, this.allRecordsAlertId);
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private isSpecifiedLevels(printOption: number): boolean {
    return (printOption === AppConstants.PDF_SPECIFIED_ACTION
      || printOption === AppConstants.PDF_SPECIFIED_WITH_DETAILS_ACTION);
  }

  get isTreeList(): boolean {
    return (this.pdfType === AppConstants.TREE_LIST_PDF
      || this.pdfType === AppConstants.TREE_LIST_WITH_DETAILS_PDF);
  }

  get isUseAllActorGroups(): boolean {
    return this.lmPrintRequestService.getConfigFeatureUseAllActorGroups();
  }

  get resource(): Resource {
    // Switch Resource to selected record when printing
    // with 'Selected Record With Details' option is chosen
    const printOption = Number(this.printForm.controls.printOption.value);
    if (printOption === AppConstants.PDF_SELECTED_WITH_DETAILS_ACTION
      && this.printInfo.dataViewSet.data.length
      && this.printInfo.selectedRows.length) {
      const listResource = <ListResource>this.printInfo.resource;
      return new ListResource({
        resourceId: this.printInfo.dataViewSet.data[this.printInfo.selectedRows[0]].resourceId,
        listName: listResource.listName
      });
    }

    return this.printInfo.resource;
  }

  public checkIfFormIsValid(): boolean {
    let isValid = true;
    if (this.atLeastOneDynamicActor) {
      isValid = this.dynamicActorsComponent.values.length > 0;
      if (!isValid) {
        const options: SohoMessageOptions = {
          title: Soho.Locale.translate('ValidationError'),
          message: Soho.Locale.translate('PDFAtLeastOneDynamicActor'),
          showCloseBtn: true };

        this.lmModalWrapperService
          .alert(options)
          .open();
      }
    }

    if (this.hasErrors)
      isValid = false;

    return isValid;
  }

  get requestData(): LMPrintRequestData {
    // Default scheduleView derived from landmark.popupmenu.js
    let value = this.printForm.controls.name.value;
    value = `${value}`;
    const scheduleView = <LMScheduleView>{ fields: { Name: { value } } };
    const requestData = <LMPrintRequestData>{
      requestType: 'schedulePdf',
      scheduleView
    };

    if (this.printInfo.dataView) {
      requestData.dataView = this.printInfo.dataView;
    }

    if (this.printInfo.selectRequestData) {
      requestData.selectRequestData = this.printInfo.selectRequestData;
    }

    if (this.printInfo.includeSearchForm && this.printInfo.filterFields?.length) {
      // Send filter fields in a format the PDF generator requires to build search tags.
      // get requestParams() will build filter field parameters to be sent in the request url itself
      // for FilterBuilder to set dataviewset filter. It is not in the same format PDF generator expects in PDFAction arguments.
      requestData.filterCriteria = this.printInfo.filterFields.map((fc) => ({ fieldId: fc.fieldId, operator: fc.operator, value: Array.isArray(fc.value) ? fc.value.join('|') : fc.value }));
    }

    if (this.printInfo.listParams && Object.keys(this.printInfo.listParams).length) {
      requestData.listParams = this.printInfo.listParams;
    }

    if (this.showDynamicActors && this.dynamicActorsComponent) {
      requestData.dynamicActors = this.dynamicActorsComponent.values;
    }
    if (this.printInfo.chartNames) {
      requestData.chartNames = this.printInfo.chartNames;
    }
    return requestData;
  }

  /**
   * Returns configured form values
   * @return
   */
  get requestParams(): LMPdfPrintParams {
    const commonParams = this.lmPrintRequestService.getCommonRequestParams(this.printInfo);
    const params = <LMPdfPrintParams>{
      output: 'pdf',
      ...this.printForm.value,
      ...commonParams
    };

    if (!params.pdfPageSize) {
      params.pdfPageSize = 'Letter';
    } 

    if (this.printInfo.selectedTabIndex) {
      params.selectedTabIndex = this.printInfo.selectedTabIndex;
    }

    if (this.printInfo.selectedPanelName) {
      params.selectedPanelName = this.printInfo.selectedPanelName;
    }

    // Lookup component does not implement ControlAccessorValue. Landmark forms does not use formgroup / formcontrol to manage model data.
    if (this.lmLookupBasicPdf && this.lmLookupBasicPdf.lookupValue) {
      params.reportDistGroup = this.lmLookupBasicPdf.lookupValue;
    }
    return params;
  }

  /**
   * Returns a set print options based on PDF type
   * @return Array of print options
   */
  getPrintOptions(): LMPrintOption[] {
    const options: { [index: string]: LMPrintOption[] } = {};
    const formatOption = (value, label) => ({ value, label: Soho.Locale.translate(label) });

    options[AppConstants.COMPOSITE_FORM_PDF] = [
      formatOption(AppConstants.PDF_SELECTED_TAB_ACTION, 'PDFSelectedTab'),
      formatOption(AppConstants.PDF_ALL_TABS_ACTION, 'PDFAllTabs'),
    ];
    options[AppConstants.LIST_WITH_DETAILS_PDF] = [
      formatOption(AppConstants.PDF_ACTION, 'PDFCurrentList'),
      formatOption(AppConstants.PDF_WITH_DETAILS_ACTION, 'PDFWithDetails'),
      formatOption(AppConstants.PDF_SELECTED_WITH_DETAILS_ACTION, 'PDFSelectedWithDetails'),
    ];
    options[AppConstants.TREE_LIST_PDF] = [
      formatOption(AppConstants.PDF_ACTION, 'PDFDefaultTree'),
      formatOption(AppConstants.PDF_EXPANDED_ACTION, 'PDFAllLevels'),
      formatOption(AppConstants.PDF_SPECIFIED_ACTION, 'PDFSpecifiedMaxLevels'),
    ];
    options[AppConstants.TREE_LIST_WITH_DETAILS_PDF] = [
      formatOption(AppConstants.PDF_ACTION, 'PDFDefaultTree'),
      formatOption(AppConstants.PDF_WITH_DETAILS_ACTION, 'PDFRootLevelsWithDetails'),
      formatOption(AppConstants.PDF_EXPANDED_ACTION, 'PDFAllLevels'),
      formatOption(AppConstants.PDF_EXPANDED_WITH_DETAILS_ACTION, 'PDFAllLevelWithDetails'),
      formatOption(AppConstants.PDF_SPECIFIED_ACTION, 'PDFSpecifiedMaxLevels'),
      formatOption(AppConstants.PDF_SPECIFIED_WITH_DETAILS_ACTION, 'PDFSpecifiedMaxLevelsWithDetails'),
      formatOption(AppConstants.PDF_SELECTED_WITH_DETAILS_ACTION, 'PDFSelectedWithDetails'),
    ];
    options[AppConstants.LIST_WITH_EXPANDABLE_CARD_ROW_PDF] = [
      formatOption(AppConstants.PDF_ACTION, 'PDFCurrentList'),
      formatOption(AppConstants.PDF_WITH_DETAILS_ACTION, 'PDFWithDetails'),
    ];

    // Exclude PDF_SELECTED_WITH_DETAILS_ACTION when there is no selected row/s
    if (!this.printInfo.selectedRows?.length) {
      options[this.pdfType] = options[this.pdfType].filter((option: LMPrintOption) => option.value !== AppConstants.PDF_SELECTED_WITH_DETAILS_ACTION);
    }

    return options[this.pdfType];
  }
  
  /**
   * Returns Toast Success Message
   * @param          if pdf is scheduled
   * @return
   */
  getSuccessMessage(scheduled: boolean, params?: LMPrintRequestParams): SohoToastOptions {
    const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate(scheduled ? 'PDFIsScheduled' : 'GeneratingPDF'), this.lmSecurityUtilService.escape(params.name));
    let message: string;
    if (params?.emptyReportOptions === AppConstants.SUPPRESS_WITHOUT_NOTIFICATION) {
      message = Soho.Locale.translate('suppressWithoutNotificationMessage');
    } else if (params?.emptyReportOptions === AppConstants.SUPPRESS_WITH_NOTIFICATION) {
      message = Soho.Locale.translate('suppressWithNotificationMessage');
    } else {
      message = Soho.Locale.translate('PleaseVisitMyPrintFiles');
    }
    return { title, message };
  }

  /**
   * Returns Toast Error Message
   * @param          if pdf is scheduled
   * @return
   */
  getErrorMessage(scheduled: boolean): SohoToastOptions {
    const title = Soho.Locale.translate(scheduled ? 'PDFScheduleError' : 'PDFGenerationError');
    const message = Soho.Locale.translate(scheduled ? 'PDFWasNotScheduled' : 'PDFWasNotGenerated');

    return { title,  message };
  }

  reportDistributionGroupChanged(event: LMFieldValueChangedEvent) {
    const key = this.lmLookupBasicPdf?.lookupValue;
    this.dynamicActorsAlert.removeAllMessages();

    if (!key) {
      this.dynamicActorsLabel = Soho.Locale.translate('PDFAdHocActors');
      this.showDynamicActors = false;
      this.atLeastOneDynamicActor = false;
      return;
    }

    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    const resourceId = encodeURIComponent(`ReportDistributionGroup(${key})`);
    const url = `${baseUrl}/form/${resourceId}`;
    const fieldList = [ 'HasDynamicActorReportDistribution', 'DynamicActorsOnlyProfiles' ];

    this.lmFromRequestService.getFieldValues(url, fieldList).subscribe(response => {
      let showActors = false;
      let requiresAtLeastOneActor = false;

      if (response?.status === 'Completed') {
        const fields = response.fields;
        if (fields.hasOwnProperty('HasDynamicActorReportDistribution') && fields.hasOwnProperty('DynamicActorsOnlyProfiles')) {
          const hasDynamicActorsFld: LMField = fields.HasDynamicActorReportDistribution;
          const onlyDynamicActorsFld: LMField = fields.DynamicActorsOnlyProfiles;
          showActors = hasDynamicActorsFld.value as boolean;
          requiresAtLeastOneActor = onlyDynamicActorsFld.value as boolean;
        }
      }

      this.dynamicActorsLabel = requiresAtLeastOneActor ? Soho.Locale.translate('PDFRequiredAdHocActors') : Soho.Locale.translate('PDFAdHocActors');
      this.showDynamicActors = showActors;
      this.atLeastOneDynamicActor = requiresAtLeastOneActor;

      if (showActors) {
        const alertMessage = requiresAtLeastOneActor ? Soho.Locale.translate('PDFNeedAtLeastOneActor') : Soho.Locale.translate('PDFAddAdditionalActors');
        this.dynamicActorsAlert.addInlineMessage(alertMessage, 'info', true, undefined, undefined, this.dynamicActorsAlertId);
      }
    });
  }

  getPdfSizeData(dataViewSet: LMDataViewSet): Array<LMPdfPageSize>{
    const pageSizes: Array<LMPdfPageSize> = [];
    if (dataViewSet && dataViewSet?.data.length > 0) {
      dataViewSet.data.forEach(pdfSize => {
        pageSizes.push({value: pdfSize.fields['PDFPageSize'].value as string, description: pdfSize.representativeText});
      });
    } else {
      // Add default Letter size if no records returned
      pageSizes.push(AppConstants.DEFAULT_PAPER_SIZE);
    }
    return pageSizes;
  }

  public getPdfSizeList(): Observable<any> {
    const pdfPageSizeURL = `${this.lmUrlUtilService.getBaseUrl()}`
        + '/list/PDFPageSize.PDFPageSizeSelectList?pageop=load&pagesize=-1';

    const pdfSizeList = this.http
        .get(pdfPageSizeURL).pipe(
            catchError(error => this.handleError(error)));
    return pdfSizeList;
  }

  private handleError(error: Response) {
    return throwError(error || 'Server error');
  }

  get myPrintForm() {
    return this.printForm.controls;
  }

  get pdfPageSize() {
    return this.myPrintForm['pdfPageSize'] as FormControl;
  }

  get pdfPageWidth() {
    return this.myPrintForm['pdfPageWidth'] as FormControl;
  }

  get pdfPageHeight() {
    return this.myPrintForm['pdfPageHeight'] as FormControl;
  }

  get hasErrors() {
    if (this.pdfPageSize.value === 'Custom') {
      this.printForm.markAllAsTouched();
      this.changeDetectionRef.detectChanges();

      return this.pdfPageHeight.errors || this.pdfPageHeight.errors;
    }

    return false;
  }

  get showEmptyReportOptions() {
    if (this.isListType()
      && (this.lmLookupBasicPdf?.lookupValue === undefined || this.lmLookupBasicPdf?.lookupValue === '')) {
      return true;
    }

    return false;
  }

  private isListType(): boolean {
    return this.printInfo.pdfType === AppConstants.REGULAR_LIST_PDF
      || this.printInfo.pdfType === AppConstants.LIST_WITH_DETAILS_PDF
      || this.printInfo.pdfType === AppConstants.TREE_LIST_PDF
      || this.printInfo.pdfType === AppConstants.TREE_LIST_WITH_DETAILS_PDF
      || this.printInfo.pdfType === AppConstants.LIST_WITH_EXPANDABLE_CARD_ROW_PDF;
  }
}
